// Valutatore di espressioni di PUNTO (costruzioni geometriche sartoriali).
//
// Mentre formula.ts valuta scalari (formulaX / formulaY), qui valutiamo un
// punto 2D intero a partire da primitive di costruzione. Serve per i punti
// definiti "perpendicolare a", "lungo la retta", "in squadra", "intersezione"
// — costruzioni che non si esprimono come x=espr, y=espr indipendenti.
//
// Grammatica:
//   pointExpr := funcCall | pointRef
//   funcCall  := IDENT '(' arg (',' arg)* ')'
//   pointRef  := IDENT                       (label di un punto già calcolato)
//   arg       := pointExpr (per parametri-punto) | scalarExpr (per scalari)
//
// I parametri scalari sono valutati con evaluateFormula di formula.ts, quindi
// possono usare misure, Tg, formule nominate ecc.

import { evaluateFormula, type EvalContext } from './formula'

export interface Vec2 {
  x: number
  y: number
}

type ArgKind = 'p' | 's' // punto | scalare

interface PrimitiveDef {
  kinds: ArgKind[]
  fn: (args: Array<Vec2 | number>) => Vec2
}

const EPS = 1e-9

function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y }
}
function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y }
}
function scale(a: Vec2, k: number): Vec2 {
  return { x: a.x * k, y: a.y * k }
}
function len(a: Vec2): number {
  return Math.hypot(a.x, a.y)
}
function norm(a: Vec2): Vec2 {
  const l = len(a)
  if (l < EPS) throw new Error('Vettore nullo: punti coincidenti')
  return { x: a.x / l, y: a.y / l }
}
// Rotazione +90° (x,y) -> (-y, x)
function rot90(a: Vec2): Vec2 {
  return { x: -a.y, y: a.x }
}

// Piede della perpendicolare da P sulla retta A-B
function footOnLine(P: Vec2, A: Vec2, B: Vec2): Vec2 {
  const AB = sub(B, A)
  const denom = AB.x * AB.x + AB.y * AB.y
  if (denom < EPS) throw new Error('Retta degenere (A=B)')
  const t = ((P.x - A.x) * AB.x + (P.y - A.y) * AB.y) / denom
  return { x: A.x + AB.x * t, y: A.y + AB.y * t }
}

// Intersezione retta A-B con retta C-D
function lineIntersect(A: Vec2, B: Vec2, C: Vec2, D: Vec2): Vec2 {
  const r = sub(B, A)
  const s = sub(D, C)
  const denom = r.x * s.y - r.y * s.x
  if (Math.abs(denom) < EPS) throw new Error('Rette parallele: nessuna intersezione')
  const t = ((C.x - A.x) * s.y - (C.y - A.y) * s.x) / denom
  return { x: A.x + r.x * t, y: A.y + r.y * t }
}

const PRIMITIVES: Record<string, PrimitiveDef> = {
  // pt(xExpr, yExpr) — costruttore esplicito da due scalari
  pt: {
    kinds: ['s', 's'],
    fn: ([x, y]) => ({ x: x as number, y: y as number }),
  },
  // offset(P, dx, dy) — trasla P di (dx, dy)
  offset: {
    kinds: ['p', 's', 's'],
    fn: ([P, dx, dy]) => add(P as Vec2, { x: dx as number, y: dy as number }),
  },
  // mid(P, Q) — punto medio
  mid: {
    kinds: ['p', 'p'],
    fn: ([P, Q]) => scale(add(P as Vec2, Q as Vec2), 0.5),
  },
  // lungo(P, Q, d) — a distanza d da P in direzione P→Q (d può essere negativo)
  lungo: {
    kinds: ['p', 'p', 's'],
    fn: ([P, Q, d]) =>
      add(P as Vec2, scale(norm(sub(Q as Vec2, P as Vec2)), d as number)),
  },
  // perp(P, A, B, d) — da P, in squadra (perpendicolare) alla retta A→B,
  // spostamento d. Segno + = lato sinistro di A→B (rot +90°).
  perp: {
    kinds: ['p', 'p', 'p', 's'],
    fn: ([P, A, B, d]) =>
      add(
        P as Vec2,
        scale(norm(rot90(sub(B as Vec2, A as Vec2))), d as number),
      ),
  },
  // piede_perp(P, A, B) — piede della perpendicolare da P sulla retta A-B
  piede_perp: {
    kinds: ['p', 'p', 'p'],
    fn: ([P, A, B]) => footOnLine(P as Vec2, A as Vec2, B as Vec2),
  },
  // interseca(A, B, C, D) — intersezione retta A-B con retta C-D
  interseca: {
    kinds: ['p', 'p', 'p', 'p'],
    fn: ([A, B, C, D]) =>
      lineIntersect(A as Vec2, B as Vec2, C as Vec2, D as Vec2),
  },
}

// Divide la lista argomenti rispettando le parentesi annidate.
function splitArgs(src: string): string[] {
  const out: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (c === '(') depth++
    else if (c === ')') depth--
    else if (c === ',' && depth === 0) {
      out.push(src.slice(start, i).trim())
      start = i + 1
    }
  }
  const last = src.slice(start).trim()
  if (last.length > 0 || out.length > 0) out.push(last)
  return out
}

function resolvePoint(token: string, ctx: EvalContext): Vec2 {
  const t = token.trim()
  // Chiamata a primitiva?
  const m = /^([A-Za-z_][A-Za-z0-9_]*)\s*\(([\s\S]*)\)$/.exec(t)
  if (m) {
    const name = m[1]
    const def = PRIMITIVES[name]
    if (!def) throw new Error(`Funzione punto sconosciuta: "${name}"`)
    const rawArgs = splitArgs(m[2])
    if (rawArgs.length !== def.kinds.length) {
      throw new Error(
        `"${name}" richiede ${def.kinds.length} argomenti, ricevuti ${rawArgs.length}`,
      )
    }
    const args: Array<Vec2 | number> = def.kinds.map((kind, i) => {
      const a = rawArgs[i]
      if (kind === 'p') return resolvePoint(a, ctx)
      return evaluateFormula(a, ctx) // scalare: delega a formula.ts
    })
    return def.fn(args)
  }
  // Riferimento a punto già calcolato (label senza punto)
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(t)) {
    const p = ctx.points[t]
    if (!p) throw new Error(`Punto "${t}" non disponibile`)
    return { x: p.x, y: p.y }
  }
  throw new Error(`Espressione di punto non valida: "${t}"`)
}

export interface PointEvalResult {
  point: Vec2 | null
  error: string | null
}

export function isPointExpr(formula: string | undefined): boolean {
  if (!formula) return false
  const t = formula.trim()
  if (!t || t === '?') return false
  // È un'espressione di punto se inizia con una primitiva nota seguita da '('
  const m = /^([A-Za-z_][A-Za-z0-9_]*)\s*\(/.exec(t)
  return !!m && m[1] in PRIMITIVES
}

export function tryEvaluatePoint(
  formula: string,
  ctx: EvalContext,
): PointEvalResult {
  try {
    return { point: resolvePoint(formula, ctx), error: null }
  } catch (e) {
    return { point: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export const POINT_PRIMITIVE_NAMES = Object.keys(PRIMITIVES)
